<section hlmCard>
  <div hlmCardHeader>
    <h2 hlmCardTitle>Eigenschaften</h2>
    @if (entry()) {
      <p hlmCardDescription>{{ title() }}</p>
    }
  </div>
  <div hlmCardContent class="space-y-4">
    @if (element(); as element) {
      @if (isText()) {
        <div class="space-y-2">
          <label hlmLabel for="el-content">Text</label>
          <app-placeholder-field
            #field
            id="el-content"
            [content]="element.content ?? ''"
            [wrap]="!!element.wrap"
            [placeholders]="placeholders()"
            [elementId]="element.id"
            (contentChange)="patch.emit({ content: $event })"
            (historyStep)="historyStep.emit($event)" />
          <div class="flex flex-wrap items-center gap-3">
            <label class="flex items-center gap-2 text-sm">
              <input type="checkbox" [checked]="!!element.wrap" (change)="onWrap($event)" />
              Mehrzeilig
            </label>
          </div>
          <p hlmMuted class="text-xs">
            Eigene Worte und Werte, die der Gutschein oder deine Einstellungen füllen (farbig). Das
            kleine + am Cursor fügt einen Wert ein. Ein Wert ohne Inhalt, etwa eine leere Nachricht,
            lässt den ganzen Text weg. Ein Doppelklick auf den Text bearbeitet ihn direkt auf der
            Seite.
          </p>
        </div>
      }

      <div class="grid grid-cols-2 gap-3">
        <div class="space-y-1">
          <label hlmLabel for="el-x">X (pt)</label>
          <input
            hlmInput
            id="el-x"
            type="number"
            step="1"
            min="0"
            [value]="element.x"
            (change)="onNumber('x', $event)" />
          <p hlmMuted class="text-xs">{{ mm(element.x) }} mm</p>
        </div>
        <div class="space-y-1">
          <label hlmLabel for="el-y">Y (pt)</label>
          <input
            hlmInput
            id="el-y"
            type="number"
            step="1"
            min="0"
            [value]="element.y"
            (change)="onNumber('y', $event)" />
          <p hlmMuted class="text-xs">{{ mm(element.y) }} mm</p>
        </div>
        <div class="space-y-1">
          <label hlmLabel for="el-w">Breite (pt)</label>
          <input
            hlmInput
            id="el-w"
            type="number"
            step="1"
            min="1"
            [value]="element.width"
            (change)="onNumber('width', $event)" />
          <p hlmMuted class="text-xs">{{ mm(element.width) }} mm</p>
        </div>
        <div class="space-y-1">
          <label hlmLabel for="el-h">Höhe (pt)</label>
          <input
            hlmInput
            id="el-h"
            type="number"
            step="1"
            min="1"
            [value]="element.height"
            (change)="onNumber('height', $event)" />
          <p hlmMuted class="text-xs">{{ mm(element.height) }} mm</p>
        </div>
      </div>

      @if (hasFont()) {
        <div class="space-y-1">
          <label hlmLabel for="el-font">Schriftart</label>
          <!-- A menu instead of a native select: every font shows its name in its own face. -->
          <button
            hlmBtn
            variant="outline"
            id="el-font"
            type="button"
            class="w-full justify-between font-normal"
            [hlmDropdownMenuTrigger]="fontMenu"
            align="start">
            <span class="truncate text-base" [style.font-family]="cssOf(font().family)">
              {{ fontLabel() }}
            </span>
            <ng-icon name="lucideChevronDown" class="shrink-0 opacity-60" />
          </button>
          <ng-template #fontMenu>
            <hlm-dropdown-menu class="max-h-80 w-72 overflow-y-auto">
              <hlm-dropdown-menu-group>
                @for (f of fonts(); track f.family) {
                  <button
                    hlmDropdownMenuItem
                    class="text-base"
                    [attr.aria-checked]="f.family === font().family"
                    (click)="onFont({ family: f.family })">
                    <ng-icon
                      name="lucideCheck"
                      class="shrink-0"
                      [class.invisible]="f.family !== font().family" />
                    <span [style.font-family]="f.css">{{ f.label }}</span>
                  </button>
                }
              </hlm-dropdown-menu-group>
            </hlm-dropdown-menu>
          </ng-template>
        </div>
        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-1">
            <label hlmLabel for="el-size">Schriftgröße (max., pt)</label>
            <input
              hlmInput
              id="el-size"
              type="number"
              step="0.5"
              [min]="limits()?.font_size_min ?? 6"
              [max]="limits()?.font_size_max ?? 120"
              [value]="font().size"
              (change)="onFontSize($event)" />
          </div>
          <div class="col-span-2 space-y-1">
            <label hlmLabel for="el-color">Farbe</label>
            <app-color-field
              id="el-color"
              label="Farbe"
              [value]="font().color"
              (valueChange)="onFont({ color: $event })" />
          </div>
        </div>
        @if (capacity(); as capacity) {
          @if (capacity.contentMax != null) {
            <p
              class="text-xs"
              [class.text-amber-700]="
                capacity.shrinksTo != null && capacity.shrinksTo >= readablePt
              "
              [class.text-destructive]="
                capacity.shrinksTo != null && capacity.shrinksTo < readablePt
              "
              [class.text-muted-foreground]="capacity.shrinksTo == null">
              Platz für etwa {{ capacity.capacity }} Zeichen bei {{ font().size }} pt. Der längste
              zu erwartende Text hat {{ capacity.contentMax }} Zeichen
              @if (capacity.shrinksTo == null) {
                – passt.
              } @else if (capacity.shrinksTo >= readablePt) {
                – würde auf etwa {{ capacity.shrinksTo }} pt verkleinert.
              } @else {
                – würde auf etwa {{ capacity.shrinksTo }} pt schrumpfen und wäre kaum lesbar. Bitte
                das Feld vergrößern.
              }
            </p>
          }
        }
        <div class="flex flex-wrap items-center gap-2">
          <div class="flex gap-1" role="group" aria-label="Schriftschnitt">
            <button
              hlmBtn
              size="sm"
              type="button"
              [variant]="font().bold ? 'default' : 'outline'"
              [attr.aria-pressed]="font().bold"
              aria-label="Fett"
              (click)="onFont({ bold: !font().bold })">
              <ng-icon name="lucideBold" />
            </button>
            <button
              hlmBtn
              size="sm"
              type="button"
              [variant]="font().italic ? 'default' : 'outline'"
              [attr.aria-pressed]="font().italic"
              aria-label="Kursiv"
              (click)="onFont({ italic: !font().italic })">
              <ng-icon name="lucideItalic" />
            </button>
          </div>
          <div class="flex gap-1" role="group" aria-label="Ausrichtung">
            @for (a of aligns; track a.value) {
              <button
                hlmBtn
                size="sm"
                type="button"
                [variant]="element.align === a.value ? 'default' : 'outline'"
                [attr.aria-pressed]="element.align === a.value"
                [attr.aria-label]="a.label"
                (click)="patch.emit({ align: a.value })">
                <ng-icon [name]="a.icon" />
              </button>
            }
          </div>
          <select
            hlmInput
            class="w-auto"
            aria-label="Vertikale Ausrichtung"
            [value]="element.valign"
            (change)="onValign($event)">
            @for (v of valigns; track v.value) {
              <option [value]="v.value" [selected]="v.value === element.valign">
                {{ v.label }}
              </option>
            }
          </select>
        </div>
      }

      @if (isBrand()) {
        <div class="space-y-1">
          <label hlmLabel for="el-brand-color">Logofarbe</label>
          <app-color-field
            id="el-brand-color"
            label="Logofarbe"
            [value]="brandColor()"
            (valueChange)="patch.emit({ brand_color: $event })" />
          @if (element.brand_color) {
            <button
              hlmBtn
              variant="link"
              size="sm"
              class="h-auto p-0"
              type="button"
              (click)="patch.emit({ brand_color: null })">
              Wieder die Hauptfarbe verwenden
            </button>
          } @else {
            <p hlmMuted class="text-xs">Folgt der Hauptfarbe deines Shops.</p>
          }
        </div>
      }
      @if (hasColors()) {
        <div class="space-y-3">
          <div class="space-y-1">
            <label hlmLabel for="qr-color">Farbe des Codes</label>
            <app-color-field
              id="qr-color"
              label="Farbe des Codes"
              [value]="qrColor()"
              (valueChange)="patch.emit({ qr_color: $event })" />
          </div>
          <div class="space-y-1">
            <label hlmLabel for="qr-background">Hintergrund</label>
            <app-color-field
              id="qr-background"
              label="Hintergrundfarbe"
              [value]="qrBackground()"
              (valueChange)="patch.emit({ qr_background: $event })" />
          </div>
          <label class="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              [checked]="element.qr_quiet_zone ?? false"
              (change)="onQrQuietZone($event)" />
            Rand in der Hintergrundfarbe um den Code
          </label>
          <button hlmBtn variant="outline" size="sm" type="button" (click)="resetQrColors()">
            Schwarz auf Weiß
          </button>
          <p hlmMuted class="text-xs">Dunkler Code auf hellem Grund scannt am sichersten.</p>
        </div>
      }

      @if (isImage() || isBox()) {
        <div class="space-y-3">
          @if (isLibraryImage()) {
            <div class="space-y-1">
              <span hlmLabel>Bild</span>
              <div class="flex items-center gap-3">
                <div class="bg-muted size-16 shrink-0 overflow-hidden rounded border">
                  @if (picture(); as p) {
                    <img [src]="p.src" alt="" class="h-full w-full object-cover" />
                  }
                </div>
                <div class="min-w-0 space-y-1">
                  @if (picture(); as p) {
                    <p hlmMuted class="text-xs">{{ p.width }} × {{ p.height }} px</p>
                  } @else {
                    <p class="text-destructive text-xs">Kein Bild gewählt</p>
                  }
                  <button
                    hlmBtn
                    variant="outline"
                    size="sm"
                    type="button"
                    (click)="pickImage.emit()">
                    Bild wechseln
                  </button>
                </div>
              </div>
            </div>
          }
          @if (properties().has('fit')) {
            <div class="space-y-1">
              <span hlmLabel>Bildanpassung</span>
              <div class="flex gap-1" role="group" aria-label="Bildanpassung">
                @for (f of fits; track f.value) {
                  <button
                    hlmBtn
                    size="sm"
                    type="button"
                    [variant]="(element.fit ?? 'cover') === f.value ? 'default' : 'outline'"
                    [attr.aria-pressed]="(element.fit ?? 'cover') === f.value"
                    (click)="patch.emit({ fit: f.value })">
                    {{ f.label }}
                  </button>
                }
              </div>
              <p hlmMuted class="text-xs">
                Füllen schneidet das Bild auf das Feld zu, Einpassen zeigt es ganz. Bei Füllen
                verschiebt ⌘ bzw. Strg + Ziehen auf dem Bild den Ausschnitt.
              </p>
            </div>
          }
          @if (isBox()) {
            <div class="space-y-1">
              <span hlmLabel>Form</span>
              <div class="flex flex-wrap gap-1" role="group" aria-label="Form">
                @for (s of boxShapes; track s.value) {
                  <button
                    hlmBtn
                    size="icon-sm"
                    type="button"
                    [variant]="boxShape() === s.value ? 'default' : 'outline'"
                    [attr.aria-pressed]="boxShape() === s.value"
                    [attr.aria-label]="s.label"
                    [hlmTooltip]="s.label"
                    (click)="setBoxShape(s.value)">
                    <ng-icon [name]="s.icon" />
                  </button>
                }
              </div>
            </div>
          }
          @if (isImage() && properties().has('shape')) {
            <div class="space-y-1">
              <span hlmLabel>Form</span>
              <div class="flex gap-1" role="group" aria-label="Form">
                @for (s of shapes; track s.value) {
                  <button
                    hlmBtn
                    size="sm"
                    type="button"
                    [variant]="(element.shape ?? 'rect') === s.value ? 'default' : 'outline'"
                    [attr.aria-pressed]="(element.shape ?? 'rect') === s.value"
                    (click)="patch.emit({ shape: s.value })">
                    {{ s.label }}
                  </button>
                }
              </div>
            </div>
          }
          @if (properties().has('fill') && boxShape() !== 'line') {
            <div class="space-y-1">
              <label hlmLabel for="el-fill">Füllung</label>
              <app-color-field
                id="el-fill"
                label="Füllfarbe"
                [value]="fill()"
                [disabled]="fill() === null"
                [placeholder]="fill() === null ? 'keine' : null"
                (valueChange)="patch.emit({ fill: $event })" />
              <label class="flex items-center gap-2 text-sm">
                <input type="checkbox" [checked]="fill() === null" (change)="onFillNone($event)" />
                Ohne Füllung (nur Rahmen)
              </label>
            </div>
          }
          @if (hasFrame()) {
            <div class="grid grid-cols-2 gap-3">
              <div class="space-y-1">
                <label hlmLabel for="el-border">{{
                  boxShape() === 'line' ? 'Stärke (pt)' : 'Rahmen (pt)'
                }}</label>
                <input
                  hlmInput
                  id="el-border"
                  type="number"
                  step="0.5"
                  min="0"
                  [max]="limits()?.border_width_max ?? 50"
                  [value]="borderWidth()"
                  (change)="onBorderWidth($event)" />
              </div>
              <div class="col-span-2 space-y-1">
                <label hlmLabel for="el-border-color">{{
                  boxShape() === 'line' ? 'Linienfarbe' : 'Rahmenfarbe'
                }}</label>
                <app-color-field
                  id="el-border-color"
                  label="Rahmenfarbe"
                  [value]="borderColor()"
                  (valueChange)="patch.emit({ border_color: $event })" />
              </div>
            </div>
          }
          @if (
            properties().has('radius') &&
            (isBox() ? boxShape() === 'rect' : (element.shape ?? 'rect') !== 'circle')
          ) {
            <div class="space-y-1">
              <label hlmLabel for="el-radius">Ecken (pt)</label>
              <input
                hlmInput
                id="el-radius"
                type="number"
                step="0.5"
                min="0"
                [max]="limits()?.border_radius_max ?? 500"
                [value]="borderRadius()"
                (change)="onBorderRadius($event)" />
            </div>
          }
          @if (entry()?.aspect_ratio ?? pictureRatio(); as ratio) {
            <button hlmBtn variant="outline" size="sm" type="button" (click)="applyAspect(ratio)">
              Seitenverhältnis übernehmen
            </button>
          }
          @if (entry()?.image_source === 'logo' || entry()?.image_source === 'banner') {
            <p hlmMuted class="text-xs">
              Zeigt das Bild aus den Einstellungen und folgt ihm, wenn es dort geändert wird.
            </p>
          }
        </div>
      }

      @if (properties().has('opacity')) {
        <div class="space-y-1">
          <label hlmLabel for="el-opacity">Deckkraft</label>
          <div class="flex items-center gap-3">
            <input
              id="el-opacity"
              type="range"
              min="10"
              max="100"
              step="5"
              class="accent-primary flex-1"
              [value]="opacityPercent()"
              (input)="onOpacity($event, true)"
              (change)="onOpacity($event, false)" />
            <span class="w-12 shrink-0 text-right text-sm whitespace-nowrap tabular-nums"
              >{{ opacityPercent() }} %</span
            >
          </div>
        </div>
      }

      @if (element.group) {
        <div class="flex items-center justify-between gap-2 rounded-md border border-dashed p-2">
          <span class="text-xs">Teil einer Gruppe, wird allein bearbeitet.</span>
          <button
            hlmBtn
            variant="link"
            size="sm"
            class="h-auto p-0"
            type="button"
            (click)="selectGroup.emit()">
            Ganze Gruppe
          </button>
        </div>
      }

      <div class="space-y-1">
        <span hlmLabel>An der Seite ausrichten</span>
        <div class="flex flex-wrap gap-1" role="group" aria-label="An der Seite ausrichten">
          @for (b of alignButtons; track b.mode) {
            <button
              hlmBtn
              variant="outline"
              size="icon-sm"
              type="button"
              [attr.aria-label]="b.label"
              [hlmTooltip]="b.label"
              (click)="alignToPage.emit(b.mode)">
              <ng-icon [name]="b.icon" />
            </button>
          }
        </div>
        <p hlmMuted class="text-xs">Mehrere Elemente: ⇧-Klick oder ein Rahmen auf der Seite.</p>
      </div>

      @if (isBrand()) {
        <p hlmMuted class="text-xs">
          Das Schenkwerk-Logo liegt immer ganz oben, nichts kann es verdecken. Es ist ein
          Pflichtelement und hat eine Mindestgröße.
        </p>
      } @else {
        <div class="space-y-1">
          <span hlmLabel>Reihenfolge</span>
          <div class="flex gap-1" role="group" aria-label="Reihenfolge">
            <button
              hlmBtn
              variant="outline"
              size="sm"
              type="button"
              [disabled]="index() === 0"
              (click)="moveBack.emit()">
              <ng-icon name="lucideArrowDownToLine" />
              Nach hinten
            </button>
            <button
              hlmBtn
              variant="outline"
              size="sm"
              type="button"
              [disabled]="index() >= count() - 1"
              (click)="moveFront.emit()">
              <ng-icon name="lucideArrowUpToLine" />
              Nach vorne
            </button>
          </div>
          <p hlmMuted class="text-xs">Vorne liegt über hinten, etwa Text auf einem Rechteck.</p>
        </div>
      }

      <p hlmMuted class="text-xs">
        Text wird nie abgeschnitten, nur verkleinert. Verbindlich ist die Vorschau (PDF).
      </p>

      <div class="flex flex-wrap gap-2">
        @if (!isBrand()) {
          <button
            hlmBtn
            variant="outline"
            size="sm"
            type="button"
            [hlmTooltip]="'Noch einmal, daneben (⌘D / Strg+D; ⌘C und ⌘V kopieren auch in andere Designs)'"
            (click)="duplicate.emit()">
            <ng-icon name="lucideCopy" />
            Duplizieren
          </button>
        }
        <button hlmBtn variant="destructive" size="sm" type="button" (click)="remove.emit()">
          <ng-icon name="lucideTrash2" />
          Element entfernen
        </button>
      </div>
    } @else {
      <p hlmMuted>Wähle ein Element auf der Vorlage aus.</p>
    }
  </div>
</section>
